import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    RadialBarChart, RadialBar, PolarAngleAxis, ResponsiveContainer,
} from "recharts";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Play, ArrowRight, Sparkles } from "lucide-react";

/**
 * ContinueLearningCard — hero-style "next action" card for the student.
 *
 * Picks the most-recently touched active enrollment and surfaces a giant
 * RadialBarChart of progress, the program title, and a primary Continue CTA.
 * Falls back to an empty-state when the student has no enrollments yet.
 */
export default function ContinueLearningCard({ enrollments = [] }) {
    const active = enrollments.filter((e) => !e.completed_at);
    const pick = active[0] || enrollments[0] || null;

    if (!pick) {
        return (
            <motion.div
                variants={{
                    hidden: { opacity: 0, y: 14 },
                    show: {
                        opacity: 1,
                        y: 0,
                        transition: { type: "spring", stiffness: 320, damping: 30 },
                    },
                }}
                className="relative h-full rounded-2xl bg-card border border-primary/10 p-8 overflow-hidden"
                data-testid="continue-learning-empty"
            >
                <div className="flex flex-col h-full justify-between min-h-[280px]">
                    <div>
                        <div className="text-[11px] uppercase tracking-[0.22em] text-accent font-semibold">
                            Start learning
                        </div>
                        <h2 className="font-display text-3xl text-primary mt-3 leading-tight">
                            Your learning journey begins here.
                        </h2>
                        <p className="mt-3 text-sm text-muted-foreground max-w-md leading-relaxed">
                            Heritage offers dozens of programs across business, health,
                            trades, and the arts. Find one that excites you.
                        </p>
                    </div>
                    <Button asChild className="bg-primary hover:bg-primary/90 mt-6 w-fit gap-2">
                        <Link to="/">
                            Explore catalog
                            <ArrowRight className="h-4 w-4" />
                        </Link>
                    </Button>
                </div>
            </motion.div>
        );
    }

    const pct = Math.round(pick.progress_percent || 0);
    const chartData = [{ name: "progress", value: pct, fill: "#D4A140" }];

    return (
        <motion.div
            variants={{
                hidden: { opacity: 0, y: 14 },
                show: {
                    opacity: 1,
                    y: 0,
                    transition: { type: "spring", stiffness: 320, damping: 30 },
                },
            }}
            className="group relative h-full rounded-2xl bg-card border border-primary/10 p-6 sm:p-8 overflow-hidden"
            data-testid="continue-learning-card"
        >
            <div
                aria-hidden
                className="pointer-events-none absolute -top-16 -right-16 h-56 w-56 rounded-full bg-accent/10 blur-3xl"
            />
            <div className="relative flex flex-col md:flex-row gap-6 items-start">
                {/* Radial progress chart */}
                <div className="relative flex-shrink-0 h-40 w-40 mx-auto md:mx-0">
                    <ResponsiveContainer width="100%" height="100%">
                        <RadialBarChart
                            cx="50%"
                            cy="50%"
                            innerRadius="72%"
                            outerRadius="100%"
                            barSize={12}
                            data={chartData}
                            startAngle={90}
                            endAngle={-270}
                        >
                            <PolarAngleAxis
                                type="number"
                                domain={[0, 100]}
                                angleAxisId={0}
                                tick={false}
                            />
                            <RadialBar
                                background={{ fill: "rgba(14, 30, 58, 0.06)" }}
                                dataKey="value"
                                cornerRadius={8}
                                isAnimationActive
                                animationDuration={900}
                                animationEasing="ease-out"
                            />
                        </RadialBarChart>
                    </ResponsiveContainer>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <div className="font-display text-4xl text-primary leading-none">
                            {pct}%
                        </div>
                        <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mt-1">
                            complete
                        </div>
                    </div>
                </div>

                {/* Meta */}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                        <Badge
                            variant="outline"
                            className="capitalize bg-primary/5 border-primary/15 text-primary font-normal"
                        >
                            <Sparkles className="h-3 w-3 mr-1 text-accent" />
                            Keep going
                        </Badge>
                        {pick.program?.subject && (
                            <Badge variant="outline" className="capitalize">
                                {pick.program.subject}
                            </Badge>
                        )}
                    </div>
                    <h2 className="font-display text-2xl sm:text-3xl text-primary tracking-tight leading-tight">
                        {pick.program?.title}
                    </h2>
                    <div className="mt-3 text-sm text-muted-foreground">
                        {pick.chapters_completed} of {pick.total_chapters} chapters
                        completed
                    </div>
                    <Button
                        asChild
                        className="mt-6 bg-primary hover:bg-primary/90 gap-2"
                        data-testid="continue-learning-button"
                    >
                        <Link to={`/learn/${pick.program_id}`}>
                            <Play className="h-4 w-4" />
                            Resume program
                        </Link>
                    </Button>
                </div>
            </div>
        </motion.div>
    );
}
